import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useTranslation } from "react-i18next";
import { reportsService } from "../../../../api";
import { Table, Button, BackArrow } from "../../../../components";
import { useUi } from "../../../../context/ui.context";
import { usePageTitle } from "../../../../context/navigationContext";

interface RevenueReport {
  id: number;
  start_date: string;
  end_date: string;
  report_month_year: string;
  total_revenue: string;
  currency_code: string;
  generating: boolean;
  processed: number;
  total_revenue_per_location: Array<{
    name: string;
    revenue: string;
  }>;
}

const RevenueReportDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { viewAlert } = useUi();
  const [t] = useTranslation();
  usePageTitle(t("REVENUE_REPORT_DETAILS"));
  const [report, setReport] = useState<RevenueReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    if (id) {
      loadReport();
    }
  }, [id]);

  const loadReport = async () => {
    try {
      setLoading(true);
      const response = await reportsService.getRevenueReportDetail(id!);
      const reportData = response.data;

      const allLocationsText = String(t("ALL_LOCATIONS"));
      if (
        reportData.total_revenue_per_location &&
        !reportData.total_revenue_per_location.find(
          (loc: any) => loc.name === allLocationsText
        )
      ) {
        reportData.total_revenue_per_location = [
          ...reportData.total_revenue_per_location,
          {
            name: allLocationsText,
            revenue: reportData.total_revenue,
          },
        ];
      }

      setReport(reportData);
    } catch (error: any) {
      viewAlert({
        severity: "error",
        message: error?.response?.data?.detail || t("FAILED_LOAD_REPORT"),
      });
      navigate("/utilities/reports/revenue-reports");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm(String(t("REGENERATE_CONFIRM")))) {
      return;
    }

    try {
      setRegenerating(true);
      await reportsService.regenerateRevenueReport(id!);
      viewAlert({
        severity: "success",
        message: t("REPORT_REGENERATION_STARTED"),
      });
      navigate("/utilities/reports/revenue-reports");
    } catch (error: any) {
      viewAlert({
        severity: "error",
        message: error?.response?.data?.detail || t("FAILED_REGENERATE_REPORT"),
      });
    } finally {
      setRegenerating(false);
    }
  };

  const formatCurrency = (value: string, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(parseFloat(value));
  };

  if (loading) {
    return (
      <Container>
        <Skeleton height={60} width={300} style={{ marginBottom: 20 }} />
        <Skeleton height={300} style={{ marginBottom: 20 }} />
        <Skeleton height={100} count={4} style={{ marginBottom: 10 }} />
      </Container>
    );
  }

  if (!report) {
    return (
      <Container>
        <ErrorMessage>{t("REPORT_NOT_FOUND")}</ErrorMessage>
      </Container>
    );
  }

  const columns = [
    { title: t("LOCATION"), key: "name" },
    { title: t("REVENUE"), key: "revenue_formatted" },
  ];

  const tableRows =
    report.total_revenue_per_location?.map((location, index) => ({
      id: index,
      name: location.name,
      revenue_formatted: formatCurrency(location.revenue, report.currency_code),
    })) || [];

  return (
    <Container>
      <BackArrow fallbackPath="/utilities/reports/revenue-reports" />
      <Header>
        <Title>
          {t("REPORT_FOR")} {report.report_month_year}
        </Title>
        <ActionButtons>
          <Button
            variant="stroke"
            size="display"
            title={regenerating ? t("REGENERATING") : `🔄 ${t("REGENERATE")}`}
            onClick={handleRegenerate}
            disabled={regenerating}
          />
        </ActionButtons>
      </Header>

      {report.generating && (
        <InfoBanner>
          <InfoIcon>ℹ️</InfoIcon>
          <InfoText>
            {t("REPORT_GENERATING_INFO", { progress: report.processed })}
          </InfoText>
        </InfoBanner>
      )}

      <Card>
        <CardTitle>{t("OVERVIEW")}</CardTitle>
        {tableRows.length > 0 ? (
          <Table columns={columns} rows={tableRows} hideCheckbox hideOther />
        ) : (
          <EmptyState>{t("NO_LOCATION_DATA")}</EmptyState>
        )}
      </Card>

      <MetadataContainer>
        <MetadataItem>
          <MetadataLabel>{t("REPORT_PERIOD")}</MetadataLabel>
          <MetadataValue>{report.report_month_year}</MetadataValue>
        </MetadataItem>
        <MetadataItem>
          <MetadataLabel>{t("START_DATE")}</MetadataLabel>
          <MetadataValue>
            {new Date(report.start_date).toLocaleDateString()}
          </MetadataValue>
        </MetadataItem>
        <MetadataItem>
          <MetadataLabel>{t("END_DATE")}</MetadataLabel>
          <MetadataValue>
            {new Date(report.end_date).toLocaleDateString()}
          </MetadataValue>
        </MetadataItem>
        <MetadataItem>
          <MetadataLabel>{t("STATUS")}</MetadataLabel>
          <StatusBadge generating={report.generating}>
            {report.generating ? t("GENERATING") : t("COMPLETED")}
          </StatusBadge>
        </MetadataItem>
      </MetadataContainer>
    </Container>
  );
};

const Container = styled.div`
  padding: 24px;
  width: 100%;
`;

const ErrorMessage = styled.div`
  padding: 20px;
  background: ${({ theme }) => theme.errors[100]};
  border: 1px solid ${({ theme }) => theme.errors[200]};
  border-radius: 8px;
  color: ${({ theme }) => theme.errors[600]};
  text-align: center;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  margin-top: 16px;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette[900]};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.primary[50]};
  border: 1px solid ${({ theme }) => theme.primary[200]};
  border-radius: 8px;
  margin-bottom: 24px;
`;

const InfoIcon = styled.span`
  font-size: 20px;
`;

const InfoText = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.primary[700]};
`;

const Card = styled.div`
  background: ${({ theme }) => theme.palette[10]};
  border: 1px solid ${({ theme }) => theme.palette[20]};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette[900]};
  margin-bottom: 16px;
`;

const EmptyState = styled.div`
  padding: 40px;
  text-align: center;
  color: ${({ theme }) => theme.palette[700]};
  font-size: 14px;
`;

const MetadataContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const MetadataItem = styled.div`
  padding: 16px;
  background: ${({ theme }) => theme.palette[10]};
  border: 1px solid ${({ theme }) => theme.palette[20]};
  border-radius: 8px;
`;

const MetadataLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.palette[700]};
  margin-bottom: 4px;
`;

const MetadataValue = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette[900]};
`;

const StatusBadge = styled.div<{ generating: boolean }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ generating, theme }) =>
    generating ? '#f59e0b' : theme.success};
  background: ${({ generating }) =>
    generating ? '#fef3c7' : '#ecfdf5'};
  border: 1px solid
    ${({ generating }) =>
      generating ? '#fde047' : '#86efac'};
`;

export default RevenueReportDetails;
